<script lang="ts" setup>
import {computed, ref} from 'vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {RouterLink, type RouteLocationRaw} from 'vue-router';
import {TableOfContentItemState} from '@/ts/types/component/table-of-content.type';

// These items may have a disabled link in some cases.
// Generally links do not provide a disable mode
// This solution follows the accessibility guide by Orange: https://a11y-guidelines.orange.com/en/articles/disable-elements/#disable-a-link

const props = defineProps<{
  index: number;
  isInactive?: boolean;
  state: TableOfContentItemState;
  title: string;
  link: RouteLocationRaw;
  itemCounterLabelKey: string;
}>();

const isLinkDisabled = computed<boolean>((): boolean => {
  return props.state === TableOfContentItemState.Locked;
});

const isAnchorLink = computed((): boolean => {
  return typeof props.link === 'string' && props.link.startsWith('#');
});

const linkElIs = computed((): string | typeof RouterLink => {
  return (isLinkDisabled.value || isAnchorLink.value) ? 'a' : RouterLink;
});

const linkEl = ref();

function focus(): void {
  (linkEl.value?.$el || linkEl.value)?.focus(); //eslint-disable-line
};

defineExpose({focus});

</script>
<template>
  <li class="session-toc-item">
    <component
      :is="linkElIs"
      ref="linkEl"
      v-ripple.center
      :to="link"
      class="link focus-visible"
      :class="`state--${state}`"
      :aria-disabled="isLinkDisabled ? isLinkDisabled : null"
      role="link"
      :tabindex="isInactive ? '-1' : null"
      :href="isLinkDisabled && !isAnchorLink ? null : link as string"
    >
      <div class="label">
        <span class="text-body-sm">{{ $t(itemCounterLabelKey, {n: index + 1}) }}</span>
        <span v-if="title" class="text-body-sm-bold">
          {{ title }}
        </span>
      </div>
      <div class="icon">
        <svg-illustration v-if="state === 'current'" name="arrow-narrow-right.svg"/>
        <svg-illustration v-else-if="state === 'completed'" name="check.svg"/>
      </div>
    </component>
  </li>
</template>

<style lang="scss" scoped>

.session-toc-item {
  list-style: none;
  position: relative;
}

.link {
  width: 100%;
  display: flex;
  padding: var(--Spacing-spacing-3);
  position: relative;
}

.state--locked {
  @include q-card;
  cursor: not-allowed;
}

.state--current {
  @include q-card-blue;
  flex-direction: row;
}

.state--completed {
  @include q-card-green;
  flex-direction: row;
}

.label {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-1);
  align-items: flex-start;
  text-align: left;
}

.icon {
  align-self: end;
  margin-left: auto;
  position: relative;
  top: 1px;
}

</style>
